import { createBrowserRouter, Navigate } from 'react-router-dom';
import { env } from '@lib/core/env';
import { DashboardLayout } from '@layouts/DashboardLayout';
import { AuthLayout } from '@layouts/AuthLayout';
import { NewUserProtectedRoute } from '@components/auth/NewUserProtectedRoute.tsx';
import { ShopifyGuard } from '@components/auth/ShopifyGuard.tsx';
import { NavigationInitializer } from '@components/common/NavigationInitializer.tsx';

// Lazy load pages for better performance
import { lazy } from 'react';
import WelcomeOnboardPage from '@pages/onboarding/WelcomeOnboardPage.tsx';
const PlanDetailsPage = lazy(() => import('@pages/plans/PlanDetailsPage'));
// YEPAI-120 WooCommerce pilot: the confirm page the yepai-for-woocommerce plugin sends the merchant to.
const WooCommerceConnectPage = lazy(() => import('@pages/integrations/WooCommerceConnectPage'));
const QuestionnairePage = lazy(() => import('@pages/questionnaire/QuestionnairePage'));
const ExternalLoginPage = lazy(() => import('@pages/auth/ExternalLoginPage'));
const ExternalRegisterPage = lazy(() => import('@pages/auth/ExternalRegisterPage'));
const ShopifyHandoffPage = lazy(() => import('@pages/auth/ShopifyHandoffPage'));
const PlatformSelectionPage = lazy(() => import('@pages/auth/PlatformSelectionPage'));

// Onboarding 2.0 registration step pages (Shopify-first flow).
const NamePage = lazy(() => import('@pages/onboarding/steps/NamePage'));
const EcommercePlatformPage = lazy(() => import('@pages/onboarding/steps/EcommercePlatformPage'));
const StoreUrlPage = lazy(() => import('@pages/onboarding/steps/StoreUrlPage'));
const BusinessInfoPage = lazy(() => import('@pages/onboarding/steps/BusinessInfoPage'));
const DemoPage = lazy(() => import('@pages/onboarding/steps/DemoPage'));
// Dev-only preview of the gated onboarding step pages (no auth gate).
const OnboardingPreviewPage = lazy(() => import('@pages/onboarding/steps/OnboardingPreviewPage'));

const OnboardingPage = lazy(() => import('@pages/onboarding/OnboardingPage'));
const ProfilePage = lazy(() => import('@pages/profile/ProfilePage'));
const AccountSettingsPage = lazy(() => import('@pages/settings/AccountSettingsPage'));
const BillingPage = lazy(() => import('@pages/settings/BillingPage'));
const CreditUsagePage = lazy(() => import('@pages/settings/CreditUsagePage'));
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@pages/auth/ForgotPasswordPage'));
// Onboarding 3.0 (Y01 sign-up / sign-in → welcome → verify → connect entry). Not linked
// from anywhere yet; v2 /auth/* is unchanged.
const SignUpV3Page = lazy(() => import('@pages/auth/v3/SignUpV3Page'));
const LoginV3Page = lazy(() => import('@pages/auth/v3/LoginV3Page'));
const ConnectStoreV3Page = lazy(() => import('@pages/auth/v3/ConnectStoreV3Page'));
// Y02 store-connect transition → Y03 store facts / first message → Y04 (not built: explicit boundary).
const StoreConnectingV3Page = lazy(() => import('@pages/auth/v3/StoreConnectingV3Page'));
const StoreConnectedV3Page = lazy(() => import('@pages/auth/v3/StoreConnectedV3Page'));
const BrandIqV3Page = lazy(() => import('@pages/auth/v3/BrandIqV3Page'));
const KnowledgeV3Page = lazy(() => import('@pages/auth/v3/KnowledgeV3Page'));
const TeamV3Page = lazy(() => import('@pages/auth/v3/TeamV3Page'));
const ChannelsPendingV3Page = lazy(() => import('@pages/auth/v3/ChannelsPendingV3Page'));
const ResetPasswordPage = lazy(() => import('@pages/auth/ResetPasswordPage'));
const EmailSentPage = lazy(() => import('@pages/auth/EmailSentPage'));
const MailchimpCallbackPage = lazy(() => import('@pages/auth/MailchimpCallbackPage'));
const HubspotCallbackPage = lazy(() => import('@pages/auth/HubspotCallbackPage'));

// Home page
const HomePage = lazy(() => import('@pages/home/HomePage'));

// Analytics pages
const SalesFunnelPage = lazy(() => import('@pages/analytics/SalesFunnelPage'));
const AutomationPage = lazy(() => import('@pages/automation/AutomationPage'));
const KnowledgeBasePage = lazy(() => import('@pages/knowledge/KnowledgeBasePage'));
const KnowledgesPage = lazy(() => import('@pages/knowledge/KnowledgesPage'));
const ProductTrainingPage = lazy(() => import('@pages/ai-training/ProductTrainingPage'));
const FeaturedCollectionsPage = lazy(() => import('@pages/ai-training/FeaturedCollectionsPage'));
const CollectionProductsPage = lazy(() => import('@pages/ai-training/CollectionProductsPage'));
const InstallationPage = lazy(() => import('@pages/installation/InstallationPage'));
const IntegrationsPage = lazy(() => import('@pages/installation/IntegrationsPage'));
const ShopifyWebChatDetailPage = lazy(() => import('@pages/installation/ShopifyWebChatDetailPage'));
const AiTeamLandingPage = lazy(() => import('@pages/ai-team/AiTeamLandingPage'));
const AgentWorkspacePage = lazy(() => import('@pages/ai-team/AgentWorkspacePage'));
const AgentChatPage = lazy(() => import('@pages/ai-team/AgentChatPage'));
const SourcingReportPrintPage = lazy(() => import('@pages/print/SourcingReportPrintPage'));
const AnnaChatPage = lazy(() => import('@pages/ai-team/anna/AnnaChatPage'));
const ContentStudioPage = lazy(() => import('@pages/ai-team/ContentStudioPage'));
const MarketingHubPage = lazy(() => import('@pages/ai-team/hub/MarketingHubPage'));
const DraftsTab = lazy(() => import('@pages/ai-team/hub/DraftsTab'));
const PublishedTab = lazy(() => import('@pages/ai-team/hub/PublishedTab'));
// [需求⑯ 定时任务·内容日历] Calendar tab（月历 + Content Pool + 详情弹窗）
const CalendarTab = lazy(() => import('@pages/ai-team/hub/CalendarTab'));
const StorePerformanceDashboard = lazy(() =>
  import('@pages/home/StorePerformanceDashboard').then(m => ({
    default: m.StorePerformanceDashboard,
  }))
);
const SeoInsightsTab = lazy(() => import('@pages/home/SeoInsightsTab'));
// [需求⑬·跨平台广告看板] Home Dashboard 新增 Ads tab（前端骨架，数据待后端统一指标层）。
const AdsInsightsTab = lazy(() => import('@pages/home/AdsInsightsTab'));
const SupportDataTab = lazy(() => import('@pages/home/SupportDataTab'));
const ProductManagementPage = lazy(() => import('@pages/ai-team/operation/ProductManagementPage'));
// const SupportSpecialistPage = lazy(() => import('@pages/ai-team/SupportSpecialistPage'));
const IntegrationDetailPage = lazy(() => import('@pages/installation/IntegrationDetailPage'));
const ComposioToolkitDetailPage = lazy(
  () => import('@pages/installation/ComposioToolkitDetailPage')
);
const GoogleReviewPage = lazy(() => import('@pages/installation/GoogleReviewPage'));
const MetaReviewPage = lazy(() => import('@pages/installation/MetaReviewPage'));
const InboxHubPage = lazy(() => import('@pages/inbox/InboxHubPage'));
// Cross-agent page (#22/#23 task centre). Reads one agent-neutral endpoint set, so it lives
// outside any single agent's section.
//
// [Infra 1.5 #19] The approval centre is gone: 审批中心废除，并入任务中心的「待处理」状态。
// The route and its lazy import are removed here. `ApprovalCenterPage.tsx` itself is deliberately
// KEPT on disk but no longer reachable — it is the only place the kb_sync Q&A edit table exists
// (`isKbSync` / `ApprovalQaPair`), and that table has to move into the task detail before the file
// can go. Deleting it now would mean rebuilding that table from scratch.
const TaskCenterPage = lazy(() => import('@pages/platform/TaskCenterPage'));
// #5.1: its own page, not a Home tab. PRD V1.8 is explicit — "不新增 Home 顶部 tab".
const StoreManagerPage = lazy(() => import('@pages/platform/StoreManagerPage'));
// [Infra 1.5] Agent 只读 feed（Maya/Oscar/Anna）—— 独立预览路由 store-manager/:agent。
const AgentFeedPage = lazy(() => import('@pages/platform/AgentFeedPage'));
// Settings Hub (#6 and the rest of the PRD's 设置 direction): global settings shared by every agent
// plus per-agent configuration, so it belongs beside the other cross-agent pages.
const SettingsHubPage = lazy(() => import('@pages/platform/SettingsHubPage'));
const MaterialHubPage = lazy(() => import('@pages/platform/MaterialHubPage'));
const AllLeadsPage = lazy(() => import('@pages/customers/AllLeadsPage'));

// Payment pages
const PaymentPage = lazy(() => import('@pages/payment/PaymentPage'));
const PaymentCallbackPage = lazy(() => import('@pages/payment/PaymentCallbackPage'));

// Configuration pages
const ConfigurationAvatarPage = lazy(() => import('@pages/configuration/ConfigurationAvatarPage'));

// Error pages
const NotFoundPage = lazy(() => import('@pages/error/NotFoundPage'));
const RouterErrorBoundary = lazy(() =>
  import('@components/error/RouterErrorBoundary').then(m => ({ default: m.RouterErrorBoundary }))
);

export const router = createBrowserRouter([
  {
    element: <NavigationInitializer />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: '/',
        element: (
          <NewUserProtectedRoute>
            <DashboardLayout />
          </NewUserProtectedRoute>
        ),
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            index: true,
            element: <Navigate to="/home" replace />,
          },
          { path: 'integrations/woocommerce/connect', element: <WooCommerceConnectPage /> },
          {
            // Home is a tab shell (PRD #1 / #2 / #3): URL-per-tab, so a tab is deep-linkable and
            // survives a refresh. Business is the index, matching what /home showed before tabs.
            path: 'home',
            element: <HomePage />,
            children: [
              {
                index: true,
                element: <StorePerformanceDashboard />,
              },
              {
                path: 'support',
                element: <SupportDataTab />,
              },
              {
                path: 'seo',
                element: <SeoInsightsTab />,
              },
              {
                // [需求⑬·跨平台广告看板] 深链 /home/ads —— 与其它 tab 一样 URL-per-tab、可刷新可分享。
                // [临时修复 2026-09-01] 本期 Ads tab 不上线：整页是写死的 Preview 假数据，后端未接入。
                // 生产隐藏了该 tab（见 HomeTabNav），这里同步拦住直连/书签，重定向回 /home，避免商家绕过
                // tab 看到假数。dev/test 仍可访问骨架。后端指标层就绪后撤掉这层 gate。
                path: 'ads',
                element: env.isProduction ? <Navigate to="/home" replace /> : <AdsInsightsTab />,
              },
            ],
          },
          {
            // PRD #2 moved the Customer Analytics submenu into Home's Customer-data tab, entirely.
            // These four redirect to the SHEET they meant rather than all landing on Overview —
            // merchants have them bookmarked, and dumping them somewhere else would look like the
            // page was deleted. Sales Funnel was never in that submenu and stays where it is.
            path: 'analytics',
            children: [
              {
                index: true,
                element: <Navigate to="/home/support" replace />,
              },
              {
                path: 'support-performance',
                element: <Navigate to="/home/support?sheet=performance" replace />,
              },
              {
                path: 'insights',
                element: <Navigate to="/home/support?sheet=chat-summary" replace />,
              },
              {
                path: 'busiest-time',
                element: <Navigate to="/home/support?sheet=busiest-time" replace />,
              },
              {
                path: 'sales-funnel',
                element: <SalesFunnelPage />,
              },
            ],
          },
          {
            path: 'onboarding',
            element: <OnboardingPage />,
          },
          {
            path: 'profile',
            element: <ProfilePage />,
          },
          {
            path: 'settings',
            children: [
              {
                index: true,
                element: <AccountSettingsPage />,
              },
              {
                // 这一页渲染的就是 LiveChatAgentSetup——和 Settings Hub 的 Integrations
                // 卡里 WhatsApp 行点 Set Up 弹出的是同一个组件。留着就是绕过 Hub 的
                // 第二扇门，和 /integrations/whatsapp 一并退役。
                path: 'config',
                element: <Navigate to="/platform/settings?setup=whatsapp#integrations" replace />,
              },
              {
                path: 'billing',
                element: <BillingPage />,
              },
              {
                path: 'subscription',
                element: <BillingPage />,
              },
              {
                path: 'credits',
                element: <CreditUsagePage />,
              },
            ],
          },
          {
            path: 'customers',
            children: [
              {
                path: 'leads',
                element: <AllLeadsPage />,
              },
              {
                // 客户对话已经在 Anna 页左栏那一列里了（PRD「Live chat / Inbox 合併」），
                // 不再单开一页。路径保留成重定向——商家有书签。
                path: 'conversation-logs',
                element: <Navigate to="/ai-team/anna/chat" replace />,
              },
            ],
          },
          {
            path: 'installation',
            element: <InstallationPage />,
          },
          {
            path: 'integrations',
            children: [
              {
                path: 'google-review',
                element: <GoogleReviewPage />,
              },
              {
                path: 'google-review/callback',
                element: <GoogleReviewPage />,
              },
              {
                path: 'meta-review',
                element: <MetaReviewPage />,
              },
              {
                path: 'meta-review/callback',
                element: <MetaReviewPage />,
              },
              {
                index: true,
                element: <IntegrationsPage />,
              },
              {
                path: 'shopify',
                element: <ShopifyWebChatDetailPage />,
              },
              {
                // Live Chat 的号码登记收进了 Settings Hub 的 Integrations 卡——那张卡的
                // WhatsApp 行点 Set Up 直接开 LiveChatAgentSetup 弹窗，是同一个表单。
                // 侧边栏入口已去，页面退役，路径保留成重定向。
                path: 'whatsapp',
                // 带 ?setup=whatsapp：这一页原本直接就是号码登记表单，只送到卡片
                // 附近等于把深链降级成"到附近"。
                element: <Navigate to="/platform/settings?setup=whatsapp#integrations" replace />,
              },
              {
                // Must come BEFORE the catch-all :integrationId route below,
                // otherwise React Router matches "composio" as an integrationId.
                path: 'composio/:slug',
                element: <ComposioToolkitDetailPage />,
              },
              {
                path: ':integrationId',
                element: <IntegrationDetailPage />,
              },
            ],
          },
          {
            path: 'ai-team',
            element: <AiTeamLandingPage />,
          },
          {
            path: 'ai-team/marketing/studio',
            element: <ContentStudioPage />,
          },
          {
            // Prototype alignment: Image Studio is retired from Marketing.
            // Keep the path registered but redirect to Maya's chat so any
            // bookmarked/pasted URL still lands somewhere sensible.
            path: 'ai-team/marketing/studio-image',
            element: <Navigate to="/ai-team/marketing/studio" replace />,
          },
          {
            path: 'ai-team/marketing/hub',
            element: <MarketingHubPage />,
            children: [
              {
                // [需求⑯] Hub 默认落 Calendar（原默认 drafts）
                index: true,
                element: <Navigate to="calendar" replace />,
              },
              {
                // [需求⑯ 定时任务·内容日历] 内容日历排期落点
                path: 'calendar',
                element: <CalendarTab />,
              },
              {
                // Brand IQ is a global setting now (#6), not a Marketing tab. Keep the path
                // registered so bookmarked/pasted Hub URLs still land on it.
                path: 'brand-iq',
                element: <Navigate to="/platform/settings#brand-iq" replace />,
              },
              {
                // [需求⑯] Drafts 已从 Hub 导航移除（由 Calendar/Content Pool 取代），路由保留兜底
                // 旧链接/书签，组件不删。
                path: 'drafts',
                element: <DraftsTab />,
              },
              {
                path: 'published',
                element: <PublishedTab />,
              },
              {
                // Channels is a global setting now, like Brand IQ above: a merchant connects
                // Instagram once, not once per agent that posts to it. `ChannelsPage` is DELETED —
                // the Integrations card's Setup already ran the same Composio flow via
                // /integrations/composio/:slug, so this was a second implementation of one
                // authorisation. Path kept registered for bookmarked/pasted Hub URLs.
                path: 'channels',
                element: <Navigate to="/platform/settings#integrations" replace />,
              },
            ],
          },
          {
            // PRD #3 moved SEO out of Marketing entirely. Merchants have this bookmarked, and the
            // repo already redirects the Brand IQ move the same way.
            path: 'ai-team/marketing/hub/seo',
            element: <Navigate to="/home/seo" replace />,
          },
          // Back-compat: old standalone routes now live as Hub tabs.
          {
            path: 'ai-team/marketing/brand-iq',
            element: <Navigate to="/platform/settings#brand-iq" replace />,
          },
          {
            // Straight to the Integrations card rather than via the Hub tab: that tab is a redirect
            // now too, and chaining one redirect into another flashes an extra screen on the way.
            path: 'ai-team/marketing/channels',
            element: <Navigate to="/platform/settings#integrations" replace />,
          },
          {
            // [Jonna 9.14] 去掉大 Hero 落地页:点 Oscar 直接进聊天。
            // 首次/恢复行为对齐 Maya(见 AgentChatPage 的「Oscar 首次进入」effect):
            // session 首次进 Oscar → 开新空会话(显示 digest/招呼语);刷新/站内回来 → 恢复上次会话。
            path: 'ai-team/operation',
            element: <Navigate to="/ai-team/operation/chat" replace />,
          },
          // Back-compat: the standalone Operation settings page is now a Settings Hub card. Kept as a
          // redirect for the same reason the Brand IQ paths above are — bookmarks, browser history and
          // links shared outside this repo. No ShopifyGuard: it only forwards, and the destination is
          // unguarded (the card's own scan action carries the installation check instead).
          {
            path: 'ai-team/operation/settings',
            element: <Navigate to="/platform/settings#operation-settings" replace />,
          },
          {
            path: 'ai-team/operation/product-management',
            element: (
              <ShopifyGuard>
                <ProductManagementPage />
              </ShopifyGuard>
            ),
          },
          {
            // Anna 的商家控制台。静态段 `anna` 在 react-router v6 的排序里高于下面那条
            // `:agentType`，不会被它吃掉——这里放在前面只是为了读起来顺。
            path: 'ai-team/anna/chat',
            element: <AnnaChatPage />,
          },
          {
            // [需求⑱] Maya 的对话入口只有 Content Studio 一个。这条路径下面的 `:agentType`
            // 通配会把它渲染成第二个聊天页——没有任何导航到得了它，草稿卡的 Approve & Publish
            // 也从没接线——所以书签和外链落在这里等于给商家一个做不完事的 Maya。
            //
            // 只重定向 marketing 这一个 type，不能删下面那条通配：Oscar 的对话页就是
            // `/ai-team/operation/chat`（侧边栏与 OperationHomePage 都在用它）。
            // 静态段优先于 `:agentType`，同 `anna/chat`。
            path: 'ai-team/marketing/chat',
            element: <Navigate to="/ai-team/marketing/studio" replace />,
          },
          {
            path: 'ai-team/:agentType/chat',
            element: (
              <ShopifyGuard>
                <AgentChatPage />
              </ShopifyGuard>
            ),
          },
          {
            path: 'ai-team/:slug',
            element: <AgentWorkspacePage />,
          },
          {
            path: 'knowledges',
            element: <KnowledgesPage />,
          },
          {
            path: 'store-manager',
            element: <StoreManagerPage />,
          },
          {
            path: 'store-manager/:agent',
            element: <AgentFeedPage />,
          },
          {
            path: 'tasks',
            element: <TaskCenterPage />,
          },
          {
            path: 'platform/settings',
            element: <SettingsHubPage />,
          },
          {
            path: 'platform/materials',
            element: <MaterialHubPage />,
          },
          {
            path: 'inbox',
            children: [
              {
                index: true,
                element: <Navigate to="/inbox/conversations" replace />,
              },
              {
                path: ':view',
                element: <InboxHubPage />,
              },
            ],
          },
          {
            // Customisation 收进了 Settings Hub 的 Customer 卡（挂件外观、身份、快捷回复
            // 都在那儿）。侧边栏入口已去，路径保留成重定向——商家有书签。
            path: 'customisation',
            element: <Navigate to="/platform/settings#customer-settings" replace />,
          },
          {
            path: 'flow',
            element: <AutomationPage />,
          },
          {
            path: 'ai-training',
            children: [
              {
                path: 'knowledge',
                element: <KnowledgeBasePage />,
              },
              {
                path: 'product',
                element: <ProductTrainingPage />,
              },
              {
                path: 'products',
                element: <ProductTrainingPage />,
              },
              {
                path: 'featured-products/:collectionName',
                element: <FeaturedCollectionsPage />,
              },
              {
                path: 'collection-products/:collectionHandle',
                element: <CollectionProductsPage />,
              },
              {
                // Preview & Test 现在长在 Anna 的对话区里（header 右上的按钮开抽屉），
                // PRD 要的就是这个：商家是在跟 Anna 说话的当口想看看顾客那头是什么样，
                // 为此跳走一整页再跳回来，中间那段对话就断了。
                //
                // 页面已删，路径保留成重定向——跟 Brand IQ、Channels 那几处的退役方式
                // 一致，商家的书签不该落到 404 上。
                //
                // 带上 ?preview=1：旧页面本来就是可以深链的，只把人送到 Anna Chat 而让
                // 抽屉关着，等于把这个能力弄丢了。收藏的是"预览"，到站就该看见预览。
                path: 'preview',
                element: <Navigate to="/ai-team/anna/chat?preview=1" replace />,
              },
            ],
          },
        ],
      },
      {
        path: '/platform-selection',
        element: (
          <NewUserProtectedRoute>
            <PlatformSelectionPage />
          </NewUserProtectedRoute>
        ),
      },
      // ---- Onboarding 2.0 step previews (dev only, no auth gate) ----
      ...(env.appEnv === 'development'
        ? [
            { path: '/preview/onboarding', element: <OnboardingPreviewPage /> },
            { path: '/preview/onboarding/:step', element: <OnboardingPreviewPage /> },
          ]
        : []),
      // ---- Onboarding 2.0 registration steps ----
      {
        path: '/name',
        element: (
          <NewUserProtectedRoute>
            <NamePage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/ecommerce-platform',
        element: (
          <NewUserProtectedRoute>
            <EcommercePlatformPage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/store-url',
        element: (
          <NewUserProtectedRoute>
            <StoreUrlPage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/business',
        element: (
          <NewUserProtectedRoute>
            <BusinessInfoPage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/demo',
        element: (
          <NewUserProtectedRoute>
            <DemoPage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/questionnaire',
        element: (
          <NewUserProtectedRoute>
            <QuestionnairePage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/configuration-avatar',
        element: (
          <NewUserProtectedRoute>
            <ConfigurationAvatarPage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/plan-details',
        element: (
          <NewUserProtectedRoute>
            <PlanDetailsPage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/payment',
        element: (
          <NewUserProtectedRoute>
            <PaymentPage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/payment/payment-callback',
        element: <PaymentCallbackPage />,
      },
      {
        path: 'welcome-onboard',
        element: (
          <NewUserProtectedRoute>
            <WelcomeOnboardPage />
          </NewUserProtectedRoute>
        ),
      },
      {
        path: '/auth',
        element: <AuthLayout />,
        errorElement: <RouterErrorBoundary />,
        children: [
          {
            path: 'login',
            element: <LoginPage />,
          },
          {
            path: 'register',
            element: <RegisterPage />,
          },
          {
            path: 'forgot-password',
            element: <ForgotPasswordPage />,
          },
          {
            path: 'reset-password',
            element: <ResetPasswordPage />,
          },
          {
            path: 'email-sent',
            element: <EmailSentPage />,
          },
          {
            path: 'external-login',
            element: <ExternalLoginPage />,
          },
          {
            path: 'external-register',
            element: <ExternalRegisterPage />,
          },
          {
            path: 'shopify',
            element: <ShopifyHandoffPage />,
          },
        ],
      },
      { path: '/v3/signup', element: <SignUpV3Page /> },
      { path: '/v3/login', element: <LoginV3Page /> },
      { path: '/v3/connect-store', element: <ConnectStoreV3Page /> },
      { path: '/v3/connecting', element: <StoreConnectingV3Page /> },
      { path: '/v3/store-connected', element: <StoreConnectedV3Page /> },
      { path: '/v3/brand-iq', element: <BrandIqV3Page /> },
      { path: '/v3/knowledge', element: <KnowledgeV3Page /> },
      { path: '/v3/team', element: <TeamV3Page /> },
      { path: '/v3/channels', element: <ChannelsPendingV3Page /> },
      {
        path: '/auth/mailchimp/callback',
        element: <MailchimpCallbackPage />,
      },
      {
        path: '/auth/hubspot/callback',
        element: <HubspotCallbackPage />,
      },
      {
        // Read-only A4 print page for the backend PDF renderer (缺口 B / 方案 C).
        // Standalone — no dashboard shell — so headless Chromium captures the report alone.
        path: '/print/sourcing-report',
        element: <SourcingReportPrintPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
